// src/pages/misc/landing/contrast/VentajeVisuals.tsx
// "Con Ventaje" side — product fragments in the landing-card style (dark panel, white lead
// pill, stage pill, glowing cyan dial). Same building blocks as HeroPreviews.
// Motion: CSS in contrast.css reads --vl-swap from the section.
//
// [HANDOFF] The three "Con Ventaje" pictures, right-hand card of each pinned beat: WorkspaceFicha (beat c1, lead card that fills itself),
//   AiInsideLead (beat ia, AI dial + pills inside a lead) and OneCloser (beat money, one closer, bars and gold ring).
//   Pure display + fake sample data (Spanish, Yucatán examples). They have no state and no timers: every animation is CSS (see the
//   big note at the top of contrast.css). To change what a beat shows, edit the slot map in contrast/index.tsx.
// [DEPENDS-ON] components/leads/LeadInitialsBadge.tsx, components/leads/LeadStageBadge.tsx (stagePillStyle), lucide-react.
import React from 'react';
import { CalendarCheck, MessageCircle, Phone, PhoneCall, Sparkles, Video } from 'lucide-react';
import LeadInitialsBadge from '../../../../components/leads/LeadInitialsBadge';
import { stagePillStyle } from '../../../../components/leads/LeadStageBadge';

/** Volume-knob arc. Fill is driven by --vl-swap in CSS (pathLength=100). */
const SwapDial: React.FC<{ size?: number; min?: number; max?: number }> = ({ size = 44, min = 20, max = 78 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className="vl-vz-dial" aria-hidden
    style={{ ['--min' as string]: min, ['--max' as string]: max }}>
    <defs>
      <linearGradient id="vl-vz-dial-g" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#BFF3FF" />
        <stop offset="100%" stopColor="#7AE4FF" />
      </linearGradient>
    </defs>
    <path d="M 20 52.8 A 24 24 0 1 1 44 52.8" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="8" strokeLinecap="round" />
    <path d="M 20 52.8 A 24 24 0 1 1 44 52.8" fill="none" stroke="url(#vl-vz-dial-g)" strokeWidth="8" strokeLinecap="round"
      pathLength={100} className="vl-vz-dial-fill" />
  </svg>
);

/* ---------- 1 · Tú vendes, Ventaje documenta ------------------------------ */

const FICHA = [
  ['Zona', 'Temozón Norte'],
  ['Interés', 'Depto. 2 recámaras'],
  ['Presupuesto', '$2.8 M'],
  ['Etapa', 'Calificado'],
] as const;

export const WorkspaceFicha: React.FC = () => (
  <div className="vl-vz vl-vz-ficha">
    <div className="vl-vz-bubble">
      Hola, vi el depa en <b>Temozón</b>. Para vivir, con mi familia. Presupuesto 2.8.
    </div>
    <div className="vl-vz-split">
      <div className="vl-vz-panel">
        <div className="vl-vz-panel-head">
          <LeadInitialsBadge name="Laura Méndez" stage="Qualified" stageColor="#8b5cf6" size={28} />
          <div className="min-w-0 flex-1">
            <p className="vl-vz-name">Laura Méndez</p>
            <p className="vl-vz-sub">WhatsApp · hoy 10:42</p>
          </div>
        </div>
        <div className="vl-vz-rows">
          {FICHA.map(([k, v], i) => (
            <div key={k} className="vl-vz-row" style={{ ['--i' as string]: i }}>
              <span className="vl-vz-k">{k}</span>
              <span className="vl-vz-v">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="vl-vz-panel vl-vz-acts">
        <span className="vl-vz-acts-tag">En esta conversación</span>
        <div className="vl-vz-acts-row">
          <span className="vl-vz-act"><Phone size={13} /><span><b>5</b> llamadas</span></span>
          <span className="vl-vz-act"><MessageCircle size={13} /><span><b>12</b> WhatsApp</span></span>
          <span className="vl-vz-act vl-vz-act-plain"><span><b>2 min</b> en responder</span></span>
          <span className="vl-vz-act"><Video size={13} /><span><b>Zoom</b> mañana 11:00</span></span>
        </div>
      </div>
    </div>
    <p className="vl-vz-ai-note">Documentado por tu secretaria IA</p>
  </div>
);

/* ---------- 2 · La IA adentro del lead ------------------------------------ */

export const AiInsideLead: React.FC = () => (
  <div className="vl-vz vl-vz-ai">
    <div className="vl-vz-pill vl-vz-pill-back">
      <LeadInitialsBadge name="Camila Ortega" stage="New" stageColor="#3b82f6" size={30} />
      <div className="vl-vz-pill-who">
        <p className="vl-vz-pill-name">Camila Ortega</p>
        <p className="vl-vz-pill-sub">+52 984 220 1184</p>
      </div>
      <div className="vl-vz-pill-metric">
        <Sparkles size={14} />
        <span className="vl-vz-pill-metric-txt">
          <b>18%</b>
          <small>prob. de cierre</small>
        </span>
      </div>
      <span className="vl-vz-stage" style={stagePillStyle('#3b82f6', 'New')}>
        <span className="font-light">1</span> Nuevo
      </span>
    </div>
    <div className="vl-vz-leadrow">
      <SwapDial size={46} />
      <div className="vl-vz-pill">
        <LeadInitialsBadge name="Carlos Mendez" stage="Qualified" stageColor="#8b5cf6" size={30} />
        <div className="vl-vz-pill-who">
          <p className="vl-vz-pill-name">Carlos Mendez</p>
          <p className="vl-vz-pill-sub">+52 984 123 4567</p>
        </div>
        <div className="vl-vz-pill-metric">
          <Sparkles size={14} />
          <span className="vl-vz-pill-metric-txt">
            <b>72%</b>
            <small>prob. de cierre</small>
          </span>
        </div>
        <span className="vl-vz-stage" style={stagePillStyle('#8b5cf6', 'Qualified')}>
          <span className="font-light">2</span> Qualified
        </span>
      </div>
    </div>
    <div className="vl-vz-panel vl-vz-role">
      <div className="vl-vz-role-head">
        <span className="vl-vz-role-tag">IA en este lead</span>
        <strong className="vl-vz-role-name">
          <span className="vl-vz-role-a">Asistente</span>
          <span className="vl-vz-role-b">Suplente</span>
        </strong>
      </div>
      <ul className="vl-vz-bullets">
        <li><MessageCircle size={14} /> Responde precio, ubicación y disponibilidad</li>
        <li><CalendarCheck size={14} /> Agenda la visita y la confirma</li>
        <li><PhoneCall size={14} /> Lo complejo te lo pasa a ti</li>
      </ul>
    </div>
  </div>
);

/* ---------- 3 · Tu mejor cerrador ----------------------------------------- */

const COLS = [
  { name: 'Nuevo', color: '#3b82f6', n: 14, p: 1 },
  { name: 'Calificado', color: '#8b5cf6', n: 9, p: 0.56 },
  { name: 'Visita', color: '#6366f1', n: 6, p: 0.36 },
  { name: 'Cierre', color: '#EAB308', n: 3, p: 0.14 },
];

export const OneCloser: React.FC = () => (
  <div className="vl-vz vl-vz-one">
    <div className="vl-vz-pipe">
      {COLS.map((c, i) => (
        <div key={c.name} className="vl-vz-col" style={{ ['--i' as string]: i, ['--p' as string]: c.p }}>
          <span className="vl-vz-col-name" style={{ color: c.color }}>{c.name}</span>
          <span className="vl-vz-col-track">
            <span className="vl-vz-col-bar" style={{ background: c.color, boxShadow: `0 0 12px ${c.color}88` }} />
          </span>
          <span className="vl-vz-col-n">{c.n}</span>
        </div>
      ))}
      <span className="vl-vz-pipe-ai">IA · seguimiento automático</span>
    </div>
    <div className="vl-vz-closer">
      <span className="vl-vz-closer-ring">
        <span className="vl-vz-closer-avatar">TC</span>
      </span>
      <div className="min-w-0">
        <p className="vl-vz-name">Tu mejor cerrador</p>
        <p className="vl-vz-sub">Solo entra a cerrar</p>
      </div>
      <div className="vl-vz-pay">
        <b>$50 mil</b>
        <span>en comisión</span>
      </div>
      <div className="vl-vz-stat">
        <span className="vl-vz-stat-old">10 sueldos</span>
        <span className="vl-vz-stat-new">1</span>
      </div>
    </div>
  </div>
);
